import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { count, share } from '../../../chrome/measure/format';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import type { CommentRow, StanceCounts } from '../api/schemas';
import { BAR_STANCES, stanceShares, type BarStance } from './compute';
import { initials } from './format';
import { pub } from './messages';

const SEGMENT_TONE: Record<BarStance, string> = {
  supportive: 'bg-primary',
  neutral: 'bg-border',
  hostile: 'bg-destructive',
};

/** Supportive, neutral and critical comments as one bar over its legend. */
function StanceBar(props: { counts: StanceCounts }): ReactNode {
  const locale = getLocale();
  const shares = stanceShares(props.counts);
  return (
    <div className="flex flex-col gap-2">
      <div role="list" aria-label={pub('comments.bar')} className="flex h-2 w-full gap-1">
        {BAR_STANCES.map((stance) => (
          <div
            key={stance}
            role="listitem"
            aria-label={`${pub(`count.${stance}`)}: ${share(shares[stance], locale)}`}
            data-stance={stance}
            className={`rounded-pill h-full ${SEGMENT_TONE[stance]}`}
            style={{ width: `${shares[stance] * 100}%` }}
          />
        ))}
      </div>
      <dl className="flex flex-wrap gap-x-4 gap-y-1">
        {BAR_STANCES.map((stance) => (
          <div key={stance} className="flex items-center gap-1.5">
            <span aria-hidden className={`size-2 rounded-full ${SEGMENT_TONE[stance]}`} />
            <dt className="text-caption text-muted-foreground">{pub(`count.${stance}`)}</dt>
            <dd data-numeric className="text-caption font-medium">
              {count(props.counts[stance], locale)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** A row never shows its stance: stance appears only as the bar's aggregate, so
 *  no individual commenter is characterised politically. */
export function CommentsBody(props: {
  rows: readonly CommentRow[];
  counts: StanceCounts;
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}): ReactNode {
  return (
    <>
      <StanceBar counts={props.counts} />
      <ul aria-label={pub('comments.list')} className="flex flex-col">
        {props.rows.map((row) => {
          const name = row.author_handle ?? pub('comments.authorAbsent');
          return (
            <li key={row.comment_key} className="flex gap-3 border-t py-3">
              <Avatar className="size-7" aria-hidden>
                <AvatarFallback>{initials(row.author_handle ?? '')}</AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-body-sm font-medium">{name}</span>
                <p className="text-body-sm text-muted-foreground">{row.text}</p>
              </div>
            </li>
          );
        })}
      </ul>
      {props.hasMore && (
        <Button
          type="button"
          variant="ghost"
          className="self-start"
          disabled={props.loadingMore}
          onClick={props.onMore}
        >
          {pub('comments.more')}
        </Button>
      )}
    </>
  );
}
